import { useState, useEffect, useRef } from "react";
import { useAuthStore } from "@/store/useAuthStore";
import { getStoredFormData } from "@/features/tutor/context/formStorage";
import { initialFormData } from "@/features/tutor/context/FormContext";
import api, { tutorApi } from "@/utils/axios-utils";
import {
  useNamePronunciationAudio,
  type NamePronunciationEntryResponse,
} from "@/hooks/useNamePronunciationAudio";
import ALL_LANGUAGE_OPTIONS from "@/constants/multilingual";
import {
  AUDIO_VOICE_MAP,
  getLocalizedName,
  getNamePronunciationKey,
  supportsNamePronunciationTraining,
} from "@/constants/audioVoice";
import { CHARACTERS } from "@/constants/character";
import {
  loadVoicePref,
  saveVoicePref,
  getVoiceProviderConfig,
} from "@/components/NameAndVoiceModal";
import Modal, { ModalSize } from "@/components/modal/Modal";
import PrimaryButton from "@/components/PrimaryButton";

const LANGUAGE_OPTIONS = ALL_LANGUAGE_OPTIONS.filter((l) => !l.multilingual);

interface QuickNamePronunciationModalProps {
  isOpen: boolean;
  onClose: () => void;
  sessionId?: string | null;
  lessonId?: number | null;
}

const QuickNamePronunciationModal = ({
  isOpen,
  onClose,
  sessionId,
  lessonId,
}: QuickNamePronunciationModalProps) => {
  const { user } = useAuthStore();

  const [voiceId, setVoiceId] = useState("chloe");
  const [lang, setLang] = useState("en");
  const [characterAvatar, setCharacterAvatar] = useState<string>("");
  const [characterName, setCharacterName] = useState<string>("");
  const [spellInput, setSpellInput] = useState("");
  const [namePronunciations, setNamePronunciations] = useState<
    Record<string, { spelling: string; trainedAt: number }>
  >({});
  const [savedEntries, setSavedEntries] = useState<
    Record<string, NamePronunciationEntryResponse>
  >({});
  const [configuredVoiceIds, setConfiguredVoiceIds] = useState<string[]>([]);
  const [isLoadingEntries, setIsLoadingEntries] = useState(false);
  const [saved, setSaved] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const studentFirstName =
    user?.displayUsername || user?.name?.split(" ")[0] || "";
  const hasFirstName = Boolean(studentFirstName);

  const voice = AUDIO_VOICE_MAP[voiceId] ?? AUDIO_VOICE_MAP["chloe"];
  const pronKey = getNamePronunciationKey(voice, lang);
  const savedEntry = savedEntries[pronKey];
  const langLabel =
    LANGUAGE_OPTIONS.find((l) => l.value === lang)?.label ?? lang;
  const langOpt = LANGUAGE_OPTIONS.find((l) => l.value === lang) ?? null;

  const {
    isSpeakingBack,
    isPlayingCurrent,
    lastPhonemes,
    audioError,
    setAudioError,
    clearLastPhonemes,
    speakBack,
    playCurrent,
    resolvePhonemes,
    stop,
  } = useNamePronunciationAudio();

  useEffect(() => {
    if (!isOpen) return;
    if (!user?.id) return;

    const pref = loadVoicePref(user.id);
    const sessionForm = getStoredFormData(initialFormData);

    const resolvedVoiceId =
      sessionForm?.voice?.voiceId && AUDIO_VOICE_MAP[sessionForm.voice.voiceId]
        ? sessionForm.voice.voiceId
        : pref.chosenVoiceId;
    const resolvedLang =
      sessionForm?.language?.value ??
      (pref.chosenLang && pref.chosenLang !== "all" ? pref.chosenLang : "en");

    const resolvedVoice =
      AUDIO_VOICE_MAP[resolvedVoiceId] ?? AUDIO_VOICE_MAP["chloe"];

    // This modal is training-only (save/speak-back/play-current all require
    // a provider_voice_id); a voice whose provider doesn't support
    // pronunciation training would otherwise open into a UI that silently
    // no-ops on save. Bail out instead of showing it.
    if (!supportsNamePronunciationTraining(resolvedVoice)) {
      onClose();
      return;
    }

    setVoiceId(resolvedVoiceId);
    setLang(resolvedLang);
    setNamePronunciations(pref.namePronunciations);
    setConfiguredVoiceIds(pref.configuredVoiceIds ?? []);
    setSpellInput(
      pref.namePronunciations[
        getNamePronunciationKey(resolvedVoice, resolvedLang)
      ]?.spelling ?? studentFirstName,
    );
    setSaved(false);
    setIsSaving(false);
    setAudioError(null);

    const char = CHARACTERS.find(
      (c) => c.id === sessionForm?.character?.characterId,
    );
    setCharacterAvatar(char?.avatar ?? "");
    setCharacterName(char?.name ?? "");

    // Merge DDB entries into namePronunciations (DDB wins)
    setIsLoadingEntries(true);
    setSavedEntries({});
    void api
      .get<Record<string, NamePronunciationEntryResponse>>(
        "/name-pronunciation/entries",
      )
      .then((res) => {
        setSavedEntries(res.data);
        const ddbNamePronunciations = Object.fromEntries(
          Object.entries(res.data).map(([key, entry]) => [
            key,
            { spelling: entry.surfaceText, trainedAt: entry.updatedAtMs ?? 0 },
          ]),
        );
        setNamePronunciations((prev) => ({
          ...prev,
          ...ddbNamePronunciations,
        }));
      })
      .catch((err) => {
        console.error(
          "[QuickNamePronunciationModal] Failed to load pronunciation entries:",
          err,
        );
      })
      .finally(() => setIsLoadingEntries(false));
  }, [isOpen, user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep spellInput in sync when namePronunciations load
  useEffect(() => {
    setSpellInput(namePronunciations[pronKey]?.spelling ?? studentFirstName);
  }, [voiceId, lang, namePronunciations, studentFirstName]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    clearLastPhonemes();
  }, [voiceId, lang]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    return () => {
      stop();
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSpellChange = (val: string) => {
    setSpellInput(val);
    clearLastPhonemes();
    setNamePronunciations((prev) => ({
      ...prev,
      [pronKey]: { spelling: val, trainedAt: prev[pronKey]?.trainedAt ?? 0 },
    }));
  };

  const handleSpeakBack = async () => {
    if (!hasFirstName || !spellInput) return;
    await speakBack(spellInput || studentFirstName, voice, lang);
  };

  const handlePlayCurrent = async () => {
    if (!hasFirstName) return;
    await playCurrent(
      savedEntry?.surfaceText || studentFirstName,
      voice,
      lang,
      savedEntry,
    );
  };

  const handleSave = async () => {
    if (!user?.id || isSaving) return;

    const { provider, providerVoiceId } = getVoiceProviderConfig(voice);
    const surfaceText = spellInput || studentFirstName;

    // Update localStorage immediately
    const newNamePronunciations = {
      ...namePronunciations,
      ...(hasFirstName
        ? { [pronKey]: { spelling: surfaceText, trainedAt: Date.now() } }
        : {}),
    };
    const newConfiguredVoiceIds =
      hasFirstName && newNamePronunciations[pronKey]?.spelling
        ? Array.from(new Set([voiceId, ...configuredVoiceIds]))
        : configuredVoiceIds;
    setConfiguredVoiceIds(newConfiguredVoiceIds);
    saveVoicePref(user.id, {
      chosenVoiceId: voiceId,
      namePronunciations: newNamePronunciations,
      configuredVoiceIds: newConfiguredVoiceIds,
      chosenLang: lang || undefined,
    });

    if (!hasFirstName || !providerVoiceId) {
      setSaved(true);
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => setSaved(false), 2000);
      return;
    }

    setIsSaving(true);
    setAudioError(null);
    try {
      const phonemesToSave =
        lastPhonemes ?? (await resolvePhonemes(surfaceText, voice, lang));
      if (!phonemesToSave) {
        setAudioError(
          "Could not generate pronunciation. Try clicking 'Speak it back' first, then save.",
        );
        return;
      }

      await api.put("/name-pronunciation/entries", {
        provider,
        providerVoiceId,
        lang,
        surfaceText,
        phonemes: phonemesToSave,
      });

      // Best-effort: push the new phonemes into the active session so they
      // take effect on the next turn without requiring a page reload.
      if (sessionId && lessonId) {
        tutorApi
          .post(`/session/update_phonemes/${sessionId}`, {
            lesson_id: lessonId,
            user_name: studentFirstName,
            user_phonemes: phonemesToSave,
          })
          .catch((err) => {
            console.warn(
              "[QuickNamePronunciationModal] Live phoneme update failed (non-fatal):",
              err,
            );
          });
      }

      setSavedEntries((prev) => ({
        ...prev,
        [pronKey]: {
          provider,
          providerVoiceId,
          lang,
          surfaceText,
          phonemes: phonemesToSave!,
          updatedAtMs: Date.now(),
        },
      }));
      setSaved(true);
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      console.error("[QuickNamePronunciationModal] Save failed:", err);
      setAudioError("Failed to save. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    stop();
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      showCloseButton={false}
      size={ModalSize.sm}
      className="p-0"
    >
      <div className="p-5 sm:p-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <button
            type="button"
            onClick={handleClose}
            className="w-9 h-9 flex items-center justify-center rounded-[10px] bg-[#E8F7F0] text-[#16A46F] flex-shrink-0 hover:bg-[#DFEEE5] transition-colors"
            aria-label="Go back"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <h2 className="font-nunito font-extrabold text-[20px] sm:text-[22px] text-[#1F2A44] leading-snug">
            How should{" "}
            <span className="text-lightgreen">
              {getLocalizedName(voice, lang)}
            </span>{" "}
            say your name?
            {isLoadingEntries && (
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                className="animate-spin text-lightgreen inline ml-2 flex-shrink-0"
              >
                <circle cx="12" cy="12" r="10" strokeOpacity="0.3" />
                <path d="M12 2a10 10 0 0 1 10 10" />
              </svg>
            )}
          </h2>
        </div>

        {/* Saved confirmation row */}
        {savedEntry && (
          <div className="flex items-center gap-1.5 mb-4">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="#1C9C6D"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="w-[14px] h-[14px] flex-shrink-0"
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
            <p className="font-mulish text-[12px] text-[#1C9C6D]">
              Saved for <strong>{getLocalizedName(voice, lang)}</strong> ·{" "}
              <strong>{langLabel}</strong>. Switching either voice or language
              means re-teaching.
            </p>
          </div>
        )}

        {/* Pronunciation preview card */}
        <div className="flex items-center gap-3 bg-green-50 border border-green-100 rounded-[10px] p-4 mb-4">
          <button
            type="button"
            onClick={isPlayingCurrent ? stop : handlePlayCurrent}
            title={isPlayingCurrent ? "Stop" : "Hear current pronunciation"}
            disabled={!hasFirstName && !isPlayingCurrent}
            className="w-10 h-10 rounded-full bg-lightgreen flex items-center justify-center flex-shrink-0 hover:bg-[#178A5E] transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {isPlayingCurrent ? (
              <svg width="10" height="10" viewBox="0 0 16 16" fill="white">
                <rect x="1" y="1" width="14" height="14" rx="1.5" />
              </svg>
            ) : (
              <svg width="10" height="12" viewBox="0 0 16 16" fill="white">
                <polygon points="3,2 13,8 3,14" />
              </svg>
            )}
          </button>
          <div className="flex-1 min-w-0">
            <p className="font-nunito font-bold text-[15px] text-[#0F172A]">
              {studentFirstName || "—"}
            </p>
          </div>
          <span className="flex items-center gap-1.5 bg-white px-2.5 py-1 rounded-full border border-green-100 flex-shrink min-w-0 overflow-hidden">
            {characterAvatar && (
              <img
                src={characterAvatar}
                alt={characterName}
                className="w-5 h-5 rounded-full object-cover flex-shrink-0"
              />
            )}
            <span className="font-nunito font-bold text-[11px] text-lightgreen whitespace-nowrap flex-shrink-0">
              {getLocalizedName(voice, lang)}
            </span>
            {langOpt && (
              <>
                <span className="text-gray-300 text-[10px] flex-shrink-0">
                  ·
                </span>
                {langOpt.icon && (
                  <img
                    src={langOpt.icon}
                    alt=""
                    className="w-4 h-4 object-contain flex-shrink-0"
                  />
                )}
                <span className="font-nunito font-bold text-[11px] text-lightgreen truncate">
                  {langOpt.label}
                </span>
              </>
            )}
          </span>
        </div>

        {/* Spell input */}
        <label className="block font-nunito font-semibold text-[13px] text-gray-500 mb-2">
          Spell it the way it should sound
        </label>
        <input
          type="text"
          value={spellInput}
          onChange={(e) => handleSpellChange(e.target.value)}
          disabled={!hasFirstName}
          placeholder={studentFirstName || "Your name"}
          className={`w-full h-[44px] px-4 border border-[#E0E0E0] rounded-[10px] font-nunito font-bold text-[15px] text-[#0F172A] focus:outline-none focus:border-[#1C9C6D] focus:shadow-[0_0_4px_0_#1C9C6D4D] ${!hasFirstName ? "bg-gray-50 cursor-not-allowed opacity-50" : "bg-white"}`}
        />
        <p className="font-mulish text-[12px] text-gray-400 mt-2 mb-4 leading-relaxed">
          Type your name the way it sounds, using hyphens for syllables. We'll
          figure out the rest.
        </p>

        {/* Speak it back */}
        <div className="bg-gray-50 rounded-[10px] p-4 mb-4">
          <button
            type="button"
            onClick={isSpeakingBack ? stop : handleSpeakBack}
            disabled={!isSpeakingBack && (!hasFirstName || !spellInput)}
            className="flex items-center gap-2 px-4 py-2 border border-lightgreen bg-white text-lightgreen font-nunito font-bold text-[13px] rounded-[8px] hover:bg-green-50 transition-colors flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSpeakingBack ? (
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                className="animate-spin"
              >
                <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
                <path d="M12 2a10 10 0 0 1 10 10" />
              </svg>
            ) : (
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="8" cy="7" r="3" />
                <path d="M3 21v-1a5 5 0 0 1 5-5h0a5 5 0 0 1 5 5v1" />
                <path d="M16 8a3 3 0 0 1 0 4" />
                <path d="M19 6a6 6 0 0 1 0 8" />
              </svg>
            )}
            Speak it back
          </button>
        </div>

        {audioError && (
          <p className="font-mulish text-[12px] text-[#C2410C] mb-3">
            {audioError}
          </p>
        )}

        {/* Footer */}
        <div className="flex items-center gap-4">
          <PrimaryButton
            onClick={handleSave}
            disabled={isSaving}
            className="!h-[52px] !px-6 !text-[16px] !rounded-[12px] !font-bold flex items-center gap-2"
          >
            {isSaving ? (
              <>
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  className="animate-spin"
                >
                  <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
                  <path d="M12 2a10 10 0 0 1 10 10" />
                </svg>
                Saving...
              </>
            ) : saved ? (
              <>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                Saved!
              </>
            ) : (
              "Save to my profile"
            )}
          </PrimaryButton>
          <button
            type="button"
            onClick={handleClose}
            className="font-nunito font-semibold text-[16px] text-lightgreen hover:text-lightgreen/80 transition-colors"
          >
            Cancel
          </button>
        </div>

        <p className="font-mulish text-[11px] text-gray-400 mt-3 text-center">
          {sessionId
            ? "Changes take effect on your next response."
            : "Changes take effect next lesson."}
        </p>
      </div>
    </Modal>
  );
};

export default QuickNamePronunciationModal;
